import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Foot from '../../components/Common/Footer/Footer';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Package,
  Truck,
  MapPin,
  Calendar,
  User,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Printer,
  Pencil,
  FileText,
  Loader2,
  Activity,
  ClipboardList,
  Briefcase
} from 'lucide-react';
import styles from './ViewMaterialReceipt.module.css';
import NotFound from '../Error/NotFound';


const ViewMaterialReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchReceiptDetails();
  }, [id]);

  const fetchReceiptDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/material-receipts/${id}`);
      if (response.data.success) {
        setReceipt(response.data.data);
      } else {
        toast.error('Failed to load receipt details');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Backend connection lost');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || 'open';
    if (s === 'completed') return { label: 'COMPLETED', style: styles.completed, dot: <CheckCircle2 size={12} /> };
    if (s === 'partial') return { label: 'PARTIAL', style: styles.partial, dot: <Clock size={12} /> };
    return { label: 'OPEN', style: styles.open, dot: <AlertCircle size={12} /> };
  };

  if (loading) {
    return (
      <div className={styles['view-receipt-container']}>
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={48} className={styles.spinner} />
            <p>Gathering shipment and quality data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return <NotFound />;
  }


  const statusConfig = getStatusConfig(receipt.status);

  return (
    <div className={`${styles['view-receipt-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'Material Receipts', path: '/receipts' },
            { label: receipt.challan_number || `MR-${receipt.id}` }
          ]} />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Material Receipt</h1>
              <p>Reconcile received inventory with LOIs (Letter of Intent)</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`/receipts/edit/${receipt.id}`)}>
                <Pencil size={18} />
                <span>Edit Receipt</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['receipt-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{receipt.challan_number || `MR-${receipt.id}`}</h1>
                <span className={`${styles['status-badge']} ${statusConfig.style}`}>
                  {statusConfig.dot}
                  {statusConfig.label}
                </span>
              </div>
            </div>

            <div className={styles['receipt-meta']}>
              <div className={styles['meta-item']}>
                <Briefcase size={16} />
                <span>Project: <strong>{receipt.purchase_order?.project?.project_name || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <User size={16} />
                <span>Vendor: <strong>{receipt.purchase_order?.vendor?.vendor_name || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Receipt Date: <strong>{new Date(receipt.received_date || receipt.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><Package size={20} /></div>
                <span className={styles['m-trend']}>Current Batch</span>
              </div>
              <div className={styles['m-info']}>
                <label>Total Received Qty</label>
                <h2>{receipt.total_quantity_received} Units</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-efficiency']}`}><Activity size={20} /></div>
                <span className={styles['m-trend']}>Inbound Logistics</span>
              </div>
              <div className={styles['m-info']}>
                <label>Transit Status</label>
                <h2>Delivered</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-quality']}`}><ShieldCheck size={20} /></div>
                <span className={styles['m-trend']}>Quality Check</span>
              </div>
              <div className={styles['m-info']}>
                <label>Clearance Level</label>
                <h2>Premium</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-items']}`}><ClipboardList size={20} /></div>
                <span className={styles['m-trend']}>Line Items</span>
              </div>
              <div className={styles['m-info']}>
                <label>Category Count</label>
                <h2>{receipt.items?.length || 0} Types</h2>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            {/* Left Column (70%) */}
            <div className={styles['left-column']}>
              {/* Logistics Section */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Truck size={18} />
                    <h3>Shipment Logistics</h3>
                  </div>
                  <span className={styles['h-badge']}>Transit Details</span>
                </div>

                <div className={styles['logistics-grid']}>
                  <div className={styles['log-item']}>
                    <label>Transporter</label>
                    <div className={styles['log-content']}>
                      <div className={styles['log-icon']}><Truck size={14} /></div>
                      <div className={styles['log-info']}>
                        <span>{receipt.transporter_name || 'Direct Delivery'}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles['log-item']}>
                    <label>Vehicle Number</label>
                    <div className={styles['log-content']}>
                      <div className={styles['log-icon']}><Activity size={14} /></div>
                      <div className={styles['log-info']}>
                        <span>{receipt.vehicle_number || '---'}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles['log-item']}>
                    <label>Storage Location</label>
                    <div className={styles['log-content']}>
                      <div className={styles['log-icon']}><MapPin size={14} /></div>
                      <div className={styles['log-info']}>
                        <span>{receipt.storage_location || 'Main Warehouse'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Specification Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <ClipboardList size={18} />
                    <h3>Quantity Reconciliation</h3>
                  </div>
                  <span className={styles['h-badge']}>Itemized List</span>
                </div>

                <div className={styles['table-scroller']}>
                  {!isMobile ? (
                    <table className={styles['data-table']}>
                      <thead>
                        <tr>
                          <th>ITEM SPECIFICATION</th>
                          <th className={styles['text-right']}>ORDERED</th>
                          <th className={styles['text-right']}>PREV. RECEIVED</th>
                          <th className={styles['text-right']}>RECEIVED NOW</th>
                          <th className={styles['text-right']}>BALANCE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receipt.items?.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className={styles['item-main']}>
                                <span className={styles['i-name']}>{item.po_item?.item_name || '---'}</span>
                                <span className={styles['i-ref']}>{item.item_remarks || 'Standard Item'}</span>
                              </div>
                            </td>
                            <td className={styles['text-right']}>{item.ordered_qty} {item.unit_name || item.po_item?.unit?.unit_name}</td>
                            <td className={styles['text-right']}>{item.prev_received_qty} {item.unit_name || item.po_item?.unit?.unit_name}</td>
                            <td className={`${styles['text-right']} ${styles.highlight}`}>
                              {item.received_qty} {item.unit_name || item.po_item?.unit?.unit_name}
                            </td>
                            <td className={`${styles['text-right']} ${styles.bold}`}>
                              {item.balance_qty} {item.unit_name || item.po_item?.unit?.unit_name}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className={styles['view-item-cards-mobile']}>
                      {receipt.items?.map((item, idx) => (
                        <div key={idx} className={styles['view-item-card-mobile']}>
                          <div className={styles['v-item-header']}>
                            <span className={styles['v-item-name']}>{item.po_item?.item_name || '---'}</span>
                            <span className={styles['v-item-unit']}>{item.unit_name || item.po_item?.unit?.unit_name}</span>
                          </div>
                          <div className={styles['v-item-stats-grid']}>
                            <div className={styles['v-stat']}>
                              <label>Ordered</label>
                              <span>{item.ordered_qty}</span>
                            </div>
                            <div className={styles['v-stat']}>
                              <label>Previous</label>
                              <span>{item.prev_received_qty}</span>
                            </div>
                            <div className={`${styles['v-stat']} ${styles['highlight']}`}>
                              <label>Received</label>
                              <span>{item.received_qty}</span>
                            </div>
                            <div className={`${styles['v-stat']} ${item.balance_qty > 0 ? styles['has-balance'] : ''}`}>
                              <label>Balance</label>
                              <span>{item.balance_qty}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Remarks Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <FileText size={18} />
                    <h3>Additional Remarks</h3>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>
                  {receipt.remarks || 'No general delivery observations and remarks recorded for this receipt.'}
                </p>
                {receipt.quality_remarks && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '12px', borderLeft: '4px solid #f97316' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#f97316', marginBottom: '4px' }}>QUALITY NOTE</label>
                    <p style={{ margin: 0, fontSize: '13px', color: '#1e293b' }}>{receipt.quality_remarks}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column (30%) */}
            <div className={styles['right-column']}>
              {/* Quality Checklist Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <ShieldCheck size={18} />
                    <h3>Quality Assurance</h3>
                  </div>
                </div>
                
                <div className={styles['quality-list']}>
                  {[
                    { label: 'Outer Packing Intact', value: receipt.is_outer_packing_intact },
                    { label: 'No Visual Damage', value: receipt.is_visual_damage_observed },
                    { label: 'Matching Specs', value: receipt.is_matching_specifications },
                    { label: 'Certifications Enclosed', value: receipt.is_certification_enclosed }
                  ].map((q, i) => (
                    <div key={i} className={`${styles['q-item']} ${q.value ? styles.pass : styles.fail}`}>
                      <span className={styles['q-label']}>{q.label}</span>
                      <span className={`${styles['q-badge']} ${q.value ? styles['b-pass'] : styles['b-fail']}`}>
                        {q.value ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                        {q.value ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <User size={18} />
                    <h3>User</h3>
                  </div>
                </div>

                <div className={styles['st-item']}>
                  <div className={styles['st-avatar']} style={{ background: '#e0f2fe', color: '#0369a1' }}>
                    {receipt.received_by?.name?.[0]?.toUpperCase() || 'R'}
                  </div>
                  <div className={styles['st-info']}>
                    <label>Created By</label>
                    <h4>{receipt.received_by?.name || '---'}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Foot />
        </main>
      </div>
    </div>
  );
};

export default ViewMaterialReceipt;
